<template>
  <div>
    <v-card elevation="0" class="px-7">
      <v-row dense align="center" justify="space-between">
        <v-col cols="12" sm="6">
          <h1 class="fs-16 primary--text font-weight-bold">
            SLA Report
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-breadcrumbs class="app-breadcrumbs py-3" :items="breadcrumbs" />
        </v-col>
      </v-row>
    </v-card>

    <v-container class="px-7" fluid>
      <v-row no-gutters align="center" justify="space-between" class="mb-3">
        <v-col cols="12" sm="6">
          <h1 class="fs-20 font-weight-bold bluedarken--text">
            SLA Report List
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn color="primary" icon large @click="fetchData()">
            <v-icon color="bluedarken" class="icon-bold icon-large" large>
              mdi-refresh
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-card :loading="loading" class="elevation-card rounded-12 mb-5">
        <v-card-title>
          <v-icon color="primary2" class="mr-2">
            mdi-web
          </v-icon>
          <span class="fs-14 font-weight-bold primary2--text">
            SLA Report
          </span>
          <v-spacer />
          <select-column-filter
            v-model="columnActive"
            :headers="headers"
            customClass="ma-0"
          />
          <v-btn
            depressed
            dark
            color="menuActive"
            class="btn-medium ml-3"
            @click="download"
            :loading="downloading"
          >
            Download
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-simple-table
            fixed-header
            class="table-taxonomy nav-table sshcf-table"
          >
            <template v-slot:default>
              <VuePerfectScrollbar
                class="drawer-menu--scroll"
                :settings="scrollSettings"
              >
                <thead>
                  <tr>
                    <template v-for="(header, index) in headers">
                      <th
                        v-if="isHeaderSelected(header)"
                        :key="header.text"
                        class="white--text"
                        :class="{
                          [`text-${header.align}`]: true,
                          [header.customClass]: !!header.customClass
                        }"
                      >
                        <span>{{ header.text }}</span>
                        <filter-column
                          v-if="header.value !== 'action'"
                          :indexItem="index"
                          :field="header.value"
                          :listFilter="getFiltered(header.value, index)"
                          :columnType="header.columnType || false"
                          :isCode="header.isCode"
                          @sortAsc="getNestedSort"
                          @searchColumn="searchCln"
                          @filterItem="searchCln"
                        />
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredItems" :key="index">
                    <template v-for="header in headers">
                      <td
                        v-if="isColumnSelected(header.value)"
                        :key="header.name"
                        :class="{
                          [header.tdClass]: true,
                          [`text-${header.align}`]: true,
                          [header.tdClass]: !!header.tdClass
                        }"
                      >
                        <router-link
                          v-if="header.linkable"
                          :to="`/sshcp/request/${item.id}`"
                          class="bluedarken--text text-link"
                        >
                          {{ item.unique_code }}
                        </router-link>
                        <template v-else-if="header.value === 'event'">
                          <span class="text-capitalize">
                            {{ item.events }}
                            <!-- {{ item.events.map(val => val.name).join(', ') }} -->
                          </span>
                        </template>
                        <template
                          v-else-if="header.value === 'current_approval'"
                        >
                          <v-menu
                            open-on-hover
                            top
                            offset-y
                            v-if="item.pending_approval.length > 1"
                          >
                            <template v-slot:activator="{ on }">
                              <div v-on="on">
                                {{ item.pending_approval.length }} Approvers
                              </div>
                            </template>
                            <v-card
                              flat
                              outlined
                              :elevation="0"
                              style="boder-radius: 25px !important"
                            >
                              <v-card-title
                                class="blue-grey lighten-5 fs-12 px-2 py-0 grey--text text--darken-1"
                              >
                                <span class="font-weight-bold">Approvers</span>
                              </v-card-title>
                              <v-card-text
                                class="fs-12 grey--text text--darken-1 pa-2"
                              >
                                <v-flex
                                  xs12
                                  v-for="(name, idx) in item.pending_approval"
                                  :key="'name' + idx"
                                >
                                  <span class="text-capitalize">
                                    - {{ name.name }}
                                  </span>
                                </v-flex>
                              </v-card-text>
                            </v-card>
                          </v-menu>
                          <div
                            v-else-if="item.pending_approval.length === 1"
                            class="text-capitalize"
                          >
                            {{ item.pending_approval[0].name.toLowerCase() }}
                          </div>
                        </template>
                        <template v-else-if="header.value === 'local_status'">
                          <span class="text-capitalize">
                            {{ item.local_status }}
                          </span>
                        </template>
                        <template v-else-if="header.value === 'action'">
                          <v-btn
                            text
                            color="primary"
                            class="text-capitalize font-weight-bold"
                            :to="`/sshcp/sla/${item.id}`"
                          >
                            <v-img
                              src="@/assets/images/icon-doc.png"
                              width="16"
                              height="16"
                              class="mr-2"
                            />
                            Details
                          </v-btn>
                        </template>
                        <span v-else>
                          {{ header | tableRowValue(item) }}
                        </span>
                      </td>
                    </template>
                  </tr>
                  <tr v-if="!items.length">
                    <td :colspan="headers.length" class="text-center">
                      {{ loading ? 'Loading...' : 'No data available' }}
                    </td>
                  </tr>
                </tbody>
              </VuePerfectScrollbar>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
// import { formatDate } from '@/utils/helpers';
import { toCurrencyWithoutSign } from '@/mixins/filter';
import SelectColumnFilter from '@/components/SelectColumnFilter';
import FilterColumn from '@/components/filterColumn';
import { exportToExcel } from '@/utils/helpers';
import moment from 'moment';

export default {
  components: {
    VuePerfectScrollbar,
    SelectColumnFilter,
    FilterColumn
  },
  data: () => ({
    scrollSettings: {
      maxScrollbarLength: 340
    },
    columnActive: [],
    breadcrumbs: [
      {
        text: 'SSHCP',
        to: '/sshcp/'
      },
      {
        text: 'Request',
        to: '/sshcp/request/'
      }
    ],
    totalItems: 0,
    options: {
      itemsPerPage: 10,
      page: 1
    },
    loadingDetail: false,
    items: [],
    filter: null,
    loading: false,
    dialog: false,
    downloading: false,
    headers: [
      {
        text: 'SSHCP Code',
        align: 'left',
        value: 'unique_code',
        linkable: true,
        customClass: 'sticky-header1',
        tdClass: 'sticky-body1'
      },
      {
        text: 'PO Number',
        align: 'left',
        value: 'po_number',
        isCode: true
      },
      {
        text: 'IO Number',
        align: 'left',
        value: 'io_number',
        isCode: true
      },
      {
        text: 'House',
        align: 'left',
        value: 'house'
      },
      {
        text: 'Region',
        align: 'left',
        value: 'region'
      },
      {
        text: 'District',
        align: 'left',
        value: 'district'
      },
      {
        text: 'Type of Activity',
        align: 'left',
        value: 'type_of_activity'
      },
      {
        text: 'Event',
        align: 'left',
        value: 'event'
      },
      {
        text: 'Total Budget',
        align: 'right',
        value: 'total_budget',
        columnType: true,
        formatter: toCurrencyWithoutSign,
        exportFormat: val => +val
      },
      {
        text: 'Cost Per Pax',
        align: 'right',
        value: 'cost_per_pax',
        columnType: true,
        formatter: toCurrencyWithoutSign,
        exportFormat: val => +val
      },
      {
        text: 'Current Pending Approver',
        align: 'center',
        value: 'current_approval'
      },
      {
        text: 'Created Date',
        align: 'center',
        value: 'created_date'
      },
      {
        text: 'Submitted Date',
        align: 'center',
        value: 'date_submitted'
      },
      {
        text: 'Fully Approved Date',
        align: 'center',
        value: 'date_fully_approved'
      },
      {
        text: 'Total Days Approve',
        align: 'center',
        value: 'total_days_approve'
      },
      {
        text: 'Submitted to Close Days',
        align: 'center',
        value: 'submitted_close_days'
      },
      {
        text: 'Status',
        align: 'left',
        value: 'local_status'
      },
      {
        text: 'Action',
        align: 'center',
        value: 'action'
      }
    ]
  }),
  computed: {
    filteredItems() {
      return this.getFilteredItems();
    },
    isSelectedAll: {
      get() {
        return this.headers
          ? this.columnActive.length === this.headers.length
          : false;
      },
      set(value) {
        const selected = [];

        if (value) {
          this.headers.forEach(function(user) {
            selected.push(user);
          });
        }

        this.columnActive = selected;
      }
    }
  },
  async mounted() {
    await this.selectAllColumn();
    this.fetchData();
  },
  methods: {
    async fetchData(params = {}) {
      this.loading = true;
      const res = await this.$store.dispatch('sshcp/getFormRequests', {
        ...params,
        filterArray: {
          field: 'status',
          data: ['submitted', 'rejected']
        },
        sort: 'desc',
        paginate: false,
        orderBy: 'updated_at'
      });
      this.loading = false;
      this.items = res.data.listFormRequest.map(item => {
        let oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
        let firstDate = item.date_submitted
          ? new Date(item.date_submitted)
          : null;
        let secondDate = item.date_fully_approved
          ? new Date(item.date_fully_approved)
          : null;
        let submittedDate = item.date_submitted
          ? new Date(item.date_submitted)
          : null;
        let closedDate = item.completed_date
          ? new Date(item.completed_date)
          : null;
        let diffDays = '';
        let closeDays = '';
        if (firstDate && secondDate) {
          diffDays = Math.round(Math.abs((firstDate - secondDate) / oneDay));
        }
        if (submittedDate && closedDate) {
          closeDays = Math.round(
            Math.abs((submittedDate - closedDate) / oneDay)
          );
        }
        return {
          ...item,
          // created_date: formatDate(item.created_date),
          // submitted_date: formatDate(item.date_submitted),
          // fully_approved_date: formatDate(item.date_fully_approved),
          total_days_approve: diffDays,
          submitted_close_days: closeDays,
          events:
            item.events.length > 0
              ? item.events.map(val => val.name).join(', ')
              : '',
          current_approval:
            item.current_approval.length > 0
              ? item.current_approval.map(val => val.name).join(' / ')
              : '',
          pending_approval: item.current_approval,
          local_status: item.local_status?.replace('_', ' ')
        };
      });
      this.totalItems = res.data.total_data;
    },
    selectAllColumn() {
      this.columnActive = this.headers;
    },
    isHeaderSelected(header) {
      return this.columnActive.map(item => item.value).includes(header.value);
    },
    isColumnSelected(name) {
      return this.columnActive.filter(item => item.value === name).length > 0;
    },
    getAscSort(obj) {
      this.items.sort(this.dynamicSort(obj.fieldName));
    },
    getNestedSort(obj) {
      this.nestedSort(obj.fieldName, this.items);
    },
    searchCln(obj) {
      this.filter = obj;
    },
    getFilteredItems() {
      const obj = this.filter;
      let arr = this.items;

      if (!obj) return arr;

      let nameField = obj.fieldName;
      let valSearch = Array.isArray(obj.value)
        ? obj.value.join('|').toLowerCase()
        : obj.value.toLowerCase();
      let result = [];
      if (!nameField.includes('.')) {
        /*search array of object*/
        if (nameField === 'start') {
          nameField = 'dateStart';
        } else if (nameField === 'end') {
          nameField = 'dateEnd';
        }
        result = arr.filter(function(a) {
          if (a[nameField] !== null) {
            if (Array.isArray(obj.value)) {
              return obj.value.includes(String(a[nameField]));
            } else {
              return a[nameField]
                .toString()
                .toLowerCase()
                .match(valSearch);
            }
          }
        });
      } else {
        /*search array of nested object*/
        let arrObj = nameField.split('.');
        if (arrObj.length === 2) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(a[arrObj[0]][arrObj[1]].toString());
              } else {
                return a[arrObj[0]][arrObj[1]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else if (arrObj.length === 3) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]][arrObj[2]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(
                  a[arrObj[0]][arrObj[1]][arrObj[2]].toString()
                );
              } else {
                return a[arrObj[0]][arrObj[1]][arrObj[2]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else if (arrObj.length === 4) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(
                  a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]].toString()
                );
              } else {
                return a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else {
          result = this.items;
        }
      }

      return result;
    },
    getFiltered(name) {
      if (!name.includes('.')) {
        return Object.keys(
          this.items.reduce((result, arr) => {
            result[arr[name]] = [...(result[arr[name]] || []), arr];
            return result;
          }, {})
        );
      } else {
        let nameField = name.split('.');
        return Object.keys(
          this.items.reduce((result, arr) => {
            if (!arr[nameField[0]]) return;
            result[arr[nameField[0]][nameField[1]]] = [
              ...(result[arr[nameField[0]][nameField[1]]] || []),
              arr
            ];
            return result;
          }, {})
        );
      }
    },
    async download() {
      this.downloading = true;
      const headers = this.columnActive.filter(item => item.value !== 'action');
      const columns = headers.map(item => item.text);
      const items = this.filteredItems.map(item =>
        headers.map(header =>
          header.formatter
            ? header.formatter(item[header.value])
            : item[header.value]
        )
      );
      const date = moment().format('YYYY-MM-DD-HHmmss');
      const fileName = `SLA-Reports-${date}`;

      await exportToExcel(columns, items, fileName);

      this.downloading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.hoverable-tr {
  cursor: pointer;

  &:hover {
    background: #dfdfdf;
  }
}

.drawer-menu--scroll {
  max-height: 600px;
  overflow: auto;
}
</style>
